import { useEffect, useId, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { ChevronDown, Loader2, Pencil, Play } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import ItemNotices from "./ItemNotices";
import PlanEditor from "./PlanEditor";
import { askEngine } from "./lib/askEngine";
import { formatNumber, itemReason, planLine, templateLabel, whyLines } from "./lib/format";
import { askFailed, estimateMinutes, fromEngine, markAsking, replaceItem } from "./lib/planEdit";
import { warmupFor } from "./lib/warmup";

// How many other templates to offer (walkthrough 3: "the next two").
const OTHER_OPTIONS = 2;

// backend/training/engine_constants.py CHECKIN_VOLUME_BELOW and
// CHECKIN_REST_BELOW: a check-in score under the first makes today the
// lighter version; under the second, rest (or Minimum) is suggested.
const CHECKIN_VOLUME_BELOW = 3.5;
const CHECKIN_REST_BELOW = 2.0;

/**
 * The check-in's effect as one line. The engine's reason already names the
 * check-in ("Check-in 3.75 of 5 — as planned.", "No check-in — as
 * planned."), so the label isn't said twice (W7).
 */
function checkinLine(checkin) {
  const reason = checkin?.reason || "None — as planned.";
  if (/^no check-in\b/i.test(reason)) return { label: false, text: reason };
  return { label: true, text: reason.replace(/^check-in\s+/i, "") };
}

/**
 * "Upper · ~58 min". An empty plan has no minutes to say: the estimate
 * would be the warm-up's alone ("Custom · ~8 min" with nothing in it).
 */
const heading = (template, minutes, count) =>
  `${templateLabel(template)} · ${count === 0 ? "no exercises yet" : `~${Math.round(minutes)} min`}`;

/**
 * A weekly target the engine moved (engine/targets.py's reasons): up
 * ("Chest: 8 → 9 sets this week — …"), a recovery session, or back to its
 * floor after a break ("…: back to 6 (MEV) — …"). Holding and starting
 * targets didn't move.
 */
const targetMoved = (m) => m.recovery || / → /.test(m.reason) || /: back to /.test(m.reason);

/**
 * This week's set targets (walkthrough 8, f4): the ones that moved, each
 * with the engine's reason as it says it, always shown; every other
 * muscle's one tap away.
 */
function WeeklyTargets({ muscles }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const moved = muscles.filter(targetMoved);
  const rest = muscles.filter((m) => !targetMoved(m));
  return (
    <section aria-labelledby={`${id}-title`} className="rounded-lg border bg-card p-4 text-sm">
      <h3 id={`${id}-title`} className="m-0 text-xs font-semibold tracking-wide text-foreground/80 uppercase">
        This week&apos;s set targets
      </h3>
      {moved.length > 0 ? (
        <ul aria-label="Targets that moved" className="m-0 mt-2 flex list-disc flex-col gap-1 pl-4">
          {moved.map((m) => (
            <li key={m.muscle}>{m.reason}</li>
          ))}
        </ul>
      ) : (
        <p className="m-0 mt-2">No muscle&apos;s weekly target moved.</p>
      )}
      {rest.length > 0 && (
        <>
          <Button
            type="button"
            variant="ghost"
            className="mt-1 -ml-3 h-11 px-3 text-primary md:h-8"
            aria-expanded={open}
            aria-controls={`${id}-rest`}
            onClick={() => setOpen(!open)}
          >
            <ChevronDown aria-hidden="true" className={cn("transition-transform", open && "rotate-180")} />
            {moved.length > 0 ? "Other muscles" : "All muscles"} ({rest.length})
          </Button>
          <ul
            id={`${id}-rest`}
            hidden={!open}
            className="m-0 flex list-disc flex-col gap-1 pl-4 text-muted-foreground"
          >
            {rest.map((m) => (
              <li key={m.muscle}>{m.reason}</li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

WeeklyTargets.propTypes = {
  /** TrainingNext.muscles: each muscle's target and the reason it's there. */
  muscles: PropTypes.arrayOf(
    PropTypes.shape({ muscle: PropTypes.string, recovery: PropTypes.bool, reason: PropTypes.string }),
  ).isRequired,
};

/**
 * One exercise as planned, its reason, and — on demand — why each number.
 * `note`: one brought in (W3) — "Getting its numbers…" until the engine
 * answers, then its notices.
 */
function PlannedItem({ item, exercise, units, note = null }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const name = item.name ?? exercise?.name ?? item.exercise;
  // the title says the name; Why doesn't say the reason again (W8)
  const reason = itemReason(item.reason, name);
  const why = whyLines(item, reason);
  const role = item.role ?? exercise?.role;
  return (
    <li className="rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <h4 className="m-0 font-display text-lg font-semibold">{name}</h4>
        {role === "main" && item.day_type && (
          <>
            {" "}
            <Badge variant="secondary">{item.day_type === "heavy" ? "Heavy day" : "Volume day"}</Badge>
          </>
        )}
        {role === "shoulder_health" && (
          <>
            {" "}
            <Badge variant="secondary">Shoulder health</Badge>
          </>
        )}
        {item.recovery && (
          <>
            {" "}
            <Badge variant="warning">Recovery</Badge>
          </>
        )}
        {/* eased: the exercise's own break; easing: also its muscle's — the
            reason says "Easing back in" either way (W10) */}
        {(item.eased || item.easing) && (
          <>
            {" "}
            <Badge variant="warning">Easing back in</Badge>
          </>
        )}
      </div>
      <p aria-live={note ? "polite" : undefined} className="mt-1 mb-0 font-medium tabular-nums">
        {note?.getting ? (
          <span className="inline-flex items-center gap-1.5">
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            Getting its numbers…
          </span>
        ) : (
          planLine(item, units, exercise)
        )}
      </p>
      {reason && <p className="mt-1 mb-0 text-sm text-muted-foreground">{reason}</p>}
      {note && note.notices.length > 0 && <ItemNotices notices={note.notices} />}
      {why.length > 0 && (
        <>
          <Button
            type="button"
            variant="ghost"
            className="mt-1 -ml-3 h-11 px-3 text-primary md:h-8"
            aria-expanded={open}
            aria-controls={`${id}-why`}
            onClick={() => setOpen(!open)}
          >
            <ChevronDown aria-hidden="true" className={cn("transition-transform", open && "rotate-180")} />
            Why these numbers <span className="sr-only">for {name}</span>
          </Button>
          {open && (
            <dl id={`${id}-why`} className="m-0 grid gap-x-3 gap-y-1 text-sm sm:grid-cols-[auto_1fr]">
              {why.map(({ key, label, text }) => (
                <div key={key} className="contents">
                  <dt className="font-medium">{label}</dt>
                  <dd className="m-0 mb-1 text-muted-foreground sm:mb-0">{text}</dd>
                </div>
              ))}
            </dl>
          )}
        </>
      )}
    </li>
  );
}

PlannedItem.propTypes = {
  item: PropTypes.object.isRequired,
  exercise: PropTypes.object,
  units: PropTypes.string.isRequired,
  note: PropTypes.shape({ getting: PropTypes.bool, notices: PropTypes.arrayOf(PropTypes.string).isRequired }),
};

/**
 * Today's plan before it starts (walkthrough 3): "Upper · ~58 min", why
 * this session, the check-in's effect, the engine's notices, a warm-up line
 * for the first main lift, and each exercise as "3 × 4–6 @ 2 in reserve ·
 * 135 lb" (or "Find your weight", D9) with its reason and — on demand — the
 * reason for every number. *Change the plan* opens the editor (walkthrough
 * 4); *Other options* re-plans as the next two templates; *Build my own*
 * starts from an empty plan; *Start this session* starts it.
 *
 * The caller owns the draft: every change comes back through a callback.
 *
 * An exercise swapped in or added goes in at once, finding its weight, and
 * the engine is asked for its numbers with the draft's `checkin` (POST
 * prescribe/, W3): "Getting its numbers…" until it answers, then the
 * engine's item — still marked "Added by you" / "Swapped in" — with its
 * notices beside it; if it can't answer, find-your-weight at 3 in reserve,
 * and it says so. An answer lands only on the very item it was asked for:
 * one swapped away, removed or planned over meanwhile isn't touched, and
 * nothing lands once this has gone. Until it lands the item is marked as
 * asking (planEdit.markAsking), in the plan and so in the draft: a reload
 * asks again — the ask is lost with the page — under the same rules, as
 * does a marked item that comes back once its ask has settled (a change
 * made from the items before the answer rendered); Start waits while any
 * item in the plan is still asking, and says so.
 */
function PlanView({
  next,
  plan,
  edited = false,
  settings,
  exercises,
  checkin = null,
  onItemsChange,
  onChooseTemplate,
  onBuildOwn,
  onUseSuggested,
  onChangeCheckin,
  onStart,
  onRetry,
  planning = null,
  planError = null,
  starting = false,
  startError = null,
  persisted = true,
}) {
  const id = useId();
  const [editing, setEditing] = useState(false);
  const byKey = useMemo(() => new Map((exercises ?? []).map((e) => [e.key, e])), [exercises]);
  const units = plan.units ?? settings?.units ?? "lb";
  const busy = planning !== null || starting;
  const custom = plan.template === "custom";
  const minutes = edited ? estimateMinutes(plan.items, plan.template) : next.estimated_minutes;
  // The minutes asked for. The engine's plan fits them; an edited one may not.
  const budget = plan.minutes ?? next.plan.minutes ?? settings?.session_minutes;
  const over = edited && budget ? Math.round(minutes) - budget : 0;
  const warmup = warmupFor(plan.items, byKey, settings, units);
  const others = (next.templates ?? [])
    .filter((option) => option.template !== next.plan.template)
    .slice(0, OTHER_OPTIONS);
  const notices = next.notices ?? [];
  const muscles = next.muscles ?? [];
  const showEditor = editing || (custom && plan.items.length === 0);
  const checkinSays = checkinLine(next.checkin);
  const score = next.checkin?.score;
  const scored = score !== null && score !== undefined;

  // --- exercises brought in: the engine's numbers for them (W3) -----------
  // What the engine said, by exercise key (a plan has each once): {next,
  // failed, notices}. Tagged with the engine answer it was asked under, so a
  // plan from another template or check-in shows nothing of it. (Still
  // getting its numbers is the item's own mark, `asking`.)
  const [brought, setBrought] = useState({});
  // Render mirrors: an answer builds on the newest plan, through the newest
  // callback (which holds the newest draft).
  const itemsRef = useRef(plan.items);
  itemsRef.current = plan.items;
  const changeRef = useRef(onItemsChange);
  changeRef.current = onItemsChange;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /**
   * Ask the engine for the numbers of `item` — one marked as asking — with
   * the draft's check-in (none for a plan from too long ago: the `checkin`
   * Today gives), and put its answer in that item's place. (Before the start
   * an item takes the engine's sets too, so there are no sets left to keep.)
   * One gone from the exercise list can't be asked about (askEngine rejects
   * without one): it falls back like a failed ask, so it never holds Start.
   */
  // Items whose ask is on its way (see the effect below).
  const inFlight = useRef(new WeakSet());
  const bringIn = async (item) => {
    const key = item.exercise;
    const askedUnder = next;
    const exercise = byKey.get(key);
    let replacement;
    let notices = [];
    let failed = false;
    try {
      try {
        const got = await askEngine(exercise, { checkin, units, settings });
        replacement = fromEngine(got.item, exercise, { slot: item.asking.slot });
        notices = got.notices;
      } catch {
        failed = true;
        replacement = askFailed(item);
      }
      if (!mounted.current) return;
      const latest = itemsRef.current;
      const changed = replaceItem(latest, item, replacement);
      // Swapped away, removed or planned over meanwhile: nothing to change.
      if (changed === latest) return;
      // a second answer landing before the next render builds on this one
      itemsRef.current = changed;
      // the engine answering, not the person changing the plan (Today keeps
      // what it said on arrival)
      changeRef.current(changed, { answered: true });
      // The item was still there, so no newer pick of this key exists.
      setBrought((current) => ({ ...current, [key]: { next: askedUnder, failed, notices } }));
    } finally {
      // Settled: answered, fallen back or dropped. Should the marked item
      // come back after this (a change made from the items before the
      // answer rendered wins over it), it's asked about again.
      inFlight.current.delete(item);
    }
  };
  // Each item marked as asking is asked about while it's in the plan and not
  // already on its way: a pick (marked as it went in), or one the draft
  // brought back after a reload. Other changes meanwhile (a move, another
  // pick) keep the same item, which is in flight, so they don't ask again —
  // nor does StrictMode's second run of this effect. An answer replaces the
  // item (no mark), so nothing is asked twice once it lands.
  const bringInRef = useRef(bringIn);
  bringInRef.current = bringIn;
  useEffect(() => {
    for (const item of plan.items) {
      if (!item.asking || inFlight.current.has(item)) continue;
      inFlight.current.add(item);
      bringInRef.current(item);
    }
  }, [plan.items]);
  const notes = useMemo(() => {
    const said = Object.fromEntries(Object.entries(brought).filter(([, note]) => note.next === next));
    for (const item of plan.items) {
      if (item.asking) said[item.exercise] = { getting: true, failed: false, notices: [] };
    }
    return said;
  }, [brought, next, plan.items]);
  // Start waits for the engine's numbers for what's in the plan: started
  // meanwhile, it would keep the find-your-weight stand-in and never hear
  // back. (One removed or swapped away no longer holds it.)
  const waitingFor = plan.items
    .filter((item) => item.asking)
    .map((item) => item.name ?? byKey.get(item.exercise)?.name ?? item.exercise);

  return (
    <section aria-labelledby={`${id}-title`} aria-busy={planning !== null || undefined} className="flex flex-col gap-4">
      <div className="rounded-lg border bg-card p-4 md:p-6">
        {/* focused when Today moves here, or the plan changes under it (a1) */}
        <h2
          id={`${id}-title`}
          tabIndex={-1}
          data-phase-heading
          className="m-0 font-display text-2xl font-semibold tabular-nums outline-none"
        >
          {heading(plan.template, minutes, plan.items.length)}
          {over > 0 && (
            <>
              {" "}
              <span className="text-base font-normal whitespace-nowrap text-foreground/80">
                — {over} min over your {budget}
              </span>
            </>
          )}
        </h2>
        <p className="mt-1 mb-0 text-muted-foreground">
          {custom ? "Your own session: add the exercises you want, in the order you want." : plan.reason}
        </p>
        {/* Each link button on a row of its own: inside a sentence its 44 px
            phone height pulled the lines apart (W12). */}
        {edited && (
          <div className="mt-2 flex flex-col items-start text-sm">
            <p className="m-0 text-muted-foreground">
              {custom ? "Built by you" : "Changed by you"} — the minutes are re-counted from your
              changes.
            </p>
            <Button
              type="button"
              variant="link"
              className="h-11 p-0 md:h-auto"
              disabled={busy}
              onClick={() => {
                setEditing(false);
                setBrought({});
                onUseSuggested();
              }}
            >
              {custom ? "Back to the suggested plan" : "Undo my changes"}
            </Button>
          </div>
        )}
        <div className="mt-3 flex flex-col items-start text-sm">
          <p className="m-0">
            {checkinSays.label && (
              <>
                <span className="font-medium">Check-in:</span>{" "}
              </>
            )}
            {checkinSays.text}
          </p>
          {scored && (
            <p className="m-0 mt-1 text-foreground/80">
              The score is the average of your answers (stress counted the other way round): under{" "}
              {formatNumber(CHECKIN_VOLUME_BELOW)} makes today lighter, under{" "}
              {formatNumber(CHECKIN_REST_BELOW)} suggests rest.
            </p>
          )}
          {edited && (
            <p id={`${id}-recheck`} className="m-0 mt-1 text-foreground/80">
              Plans again from your answers — your changes to this one are dropped.
            </p>
          )}
          <Button
            type="button"
            variant="link"
            className="h-11 p-0 md:h-auto"
            disabled={busy}
            aria-describedby={edited ? `${id}-recheck` : undefined}
            onClick={onChangeCheckin}
          >
            Change the check-in
          </Button>
        </div>
      </div>

      {planning !== null && (
        <p role="status" className="m-0 flex items-center gap-2 text-sm text-foreground/80">
          <Loader2 className="size-4 animate-spin" aria-hidden="true" />
          {planning ? `Planning a ${templateLabel(planning)} session instead…` : "Planning…"}
        </p>
      )}
      {planError && (
        <Alert variant="destructive">
          <AlertDescription className="flex flex-wrap items-center gap-3">
            <span className="flex-1">{planError}</span>
            {onRetry && (
              <Button
                size="sm"
                variant="outline"
                className="h-11 md:h-8"
                disabled={starting}
                onClick={onRetry}
              >
                Retry
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}

      {notices.length > 0 && (
        <Alert role="status">
          <AlertDescription>
            {/* The engine's notices describe its plan, not an empty one of
                your own (W11). */}
            {custom && (
              <>
                <p className="m-0 font-medium text-foreground">About the suggested plan:</p>{" "}
              </>
            )}
            <ul className="m-0 flex list-disc flex-col gap-1 pl-4 text-foreground">
              {notices.map((notice) => (
                <li key={notice}>{notice}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {muscles.length > 0 && <WeeklyTargets muscles={muscles} />}

      <div className={cn("flex flex-col gap-3 transition-opacity", planning !== null && "opacity-50")}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="m-0 text-xs font-semibold tracking-wide text-foreground/80 uppercase">
            {plan.items.length} {plan.items.length === 1 ? "exercise" : "exercises"}
          </h3>
          {!(custom && plan.items.length === 0) && (
            <Button
              type="button"
              variant={showEditor ? "default" : "outline"}
              className="h-11 md:h-9"
              aria-expanded={showEditor}
              aria-controls={`${id}-items`}
              disabled={busy}
              onClick={() => setEditing(!showEditor)}
            >
              <Pencil aria-hidden="true" />
              {showEditor ? "Done changing" : "Change the plan"}
            </Button>
          )}
        </div>

        {warmup && !showEditor && (
          <p className="m-0 rounded-lg border bg-card p-4 text-sm">
            <span className="font-medium">Warm-up for {warmup.name}</span> (not logged):{" "}
            <span className="tabular-nums">{warmup.line}</span>
          </p>
        )}

        {/* What "Change the plan" discloses: the list, or the editor. */}
        <div id={`${id}-items`}>
          {showEditor ? (
            <PlanEditor
              items={plan.items}
              exercises={exercises}
              settings={settings}
              units={units}
              notes={notes}
              // a pick goes in marked as asking; the effect above asks
              onChange={(items, picked) =>
                onItemsChange(picked ? replaceItem(items, picked.item, markAsking(picked.item, picked.slot)) : items)
              }
              disabled={busy}
            />
          ) : (
            <ol aria-label="Today's exercises" className="m-0 flex list-none flex-col gap-2 p-0">
              {plan.items.map((item, index) => (
                <PlannedItem
                  key={`${item.exercise}#${index}`}
                  item={item}
                  exercise={byKey.get(item.exercise)}
                  units={units}
                  note={notes[item.exercise] ?? null}
                />
              ))}
            </ol>
          )}
        </div>
      </div>

      {!persisted && (
        <p className="m-0 text-sm text-foreground/80" role="status">
          This plan isn&apos;t being kept on this device (its storage is blocked) — a reload
          loses your changes.
        </p>
      )}
      {startError && (
        <Alert variant="destructive">
          <AlertDescription>{startError}</AlertDescription>
        </Alert>
      )}
      <div>
        <Button
          type="button"
          className="h-11 w-full md:h-10 md:w-auto"
          disabled={busy || waitingFor.length > 0}
          onClick={onStart}
        >
          {starting ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Play aria-hidden="true" />}
          {starting ? "Starting…" : "Start this session"}
        </Button>
        <p className="mt-2 mb-0 text-sm text-foreground/80">
          {waitingFor.length > 0 &&
            `Start waits for ${waitingFor.join(" and ")}'s numbers — or remove ${waitingFor.length === 1 ? "it" : "them"} to start without ${waitingFor.length === 1 ? "it" : "them"}.`}
          {waitingFor.length === 0 &&
            (plan.items.length === 0
              ? "Starts an empty session — add exercises as you go."
              : "Opens the session: log each set as you go, and the rest timer starts after each one. You can still change anything.")}
        </p>
      </div>

      <section aria-labelledby={`${id}-others`} className="flex flex-col gap-2 border-t pt-4">
        <h3 id={`${id}-others`} className="m-0 font-display text-lg font-semibold">
          Other options
        </h3>
        <p className="m-0 text-sm text-foreground/80">
          Each plans that session instead{edited ? " — your changes to this one are dropped" : ""}.
        </p>
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {others.map((option) => (
            <li key={option.template} className="rounded-lg border bg-card p-4">
              <div className="font-medium">{templateLabel(option.template)}</div>
              <p className="mt-1 mb-2 text-sm text-muted-foreground">{option.reason}</p>
              <Button
                type="button"
                variant="outline"
                className="h-11 md:h-9"
                disabled={busy}
                onClick={() => {
                  setEditing(false);
                  onChooseTemplate(option.template);
                }}
              >
                Plan {templateLabel(option.template)} instead
              </Button>
            </li>
          ))}
          {!custom && (
            <li className="rounded-lg border bg-card p-4">
              <div className="font-medium">Build my own</div>
              <p className="mt-1 mb-2 text-sm text-muted-foreground">
                An empty plan: pick the exercises yourself. Each gets its numbers from your history, as a
                planned one would.
              </p>
              <Button
                type="button"
                variant="outline"
                className="h-11 md:h-9"
                disabled={busy}
                onClick={() => {
                  setEditing(true);
                  onBuildOwn();
                }}
              >
                Build my own
              </Button>
            </li>
          )}
        </ul>
      </section>
    </section>
  );
}

const planShape = PropTypes.shape({
  template: PropTypes.string.isRequired,
  units: PropTypes.string,
  minutes: PropTypes.number,
  reason: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
});

PlanView.propTypes = {
  /** The engine's answer (TrainingNext): estimate, check-in, templates, notices. */
  next: PropTypes.shape({
    plan: planShape.isRequired,
    estimated_minutes: PropTypes.number,
    checkin: PropTypes.shape({ score: PropTypes.number, reason: PropTypes.string }),
    templates: PropTypes.arrayOf(
      PropTypes.shape({ template: PropTypes.string, reason: PropTypes.string }),
    ),
    notices: PropTypes.arrayOf(PropTypes.string),
    muscles: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  /** What Start sends — the engine's plan, or the person's edit of it. */
  plan: planShape.isRequired,
  /** The plan differs from the engine's. */
  edited: PropTypes.bool,
  settings: PropTypes.object.isRequired,
  exercises: PropTypes.arrayOf(PropTypes.object).isRequired,
  /** The draft's check-in (TrainingCheckin) — what an exercise brought in is prescribed under. */
  checkin: PropTypes.object,
  /**
   * Called with the new items — and `{answered: true}` when it's the engine's
   * numbers landing for an exercise brought in, not the person's change.
   */
  onItemsChange: PropTypes.func.isRequired,
  onChooseTemplate: PropTypes.func.isRequired,
  onBuildOwn: PropTypes.func.isRequired,
  onUseSuggested: PropTypes.func.isRequired,
  onChangeCheckin: PropTypes.func.isRequired,
  onStart: PropTypes.func.isRequired,
  /** Retry the last plan request that failed. */
  onRetry: PropTypes.func,
  /** A re-plan in flight: the template asked for ("" for the default), else null. */
  planning: PropTypes.string,
  planError: PropTypes.string,
  starting: PropTypes.bool,
  startError: PropTypes.string,
  /** False while storage refuses the draft. */
  persisted: PropTypes.bool,
};

export default PlanView;
